/**
 * Track Detail Page
 * 
 * Displays a single track with player, metadata, and engagement options.
 * Handles both DSP-linked tracks and direct uploads.
 * ALWAYS shows a play option - either embed, audio player, or external link.
 * 
 * Route: /track/:trackId
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API, API_BASE_URL } from '../config/api';
import UserAvatar from '../components/UserAvatar';
import { getProfileUrl } from '../utils/profileUtils';

import { 
  Play, Pause, Heart, Share2, ExternalLink, Music, Gift,
  ArrowLeft, Loader2, Clock, BarChart3, MessageCircle,
  Volume2, VolumeX, SkipBack, SkipForward, Repeat,
  Shuffle, Download, ListMusic, User, Calendar, AlertCircle, Trophy,
  ShoppingCart, DollarSign, Tag, X, Check
} from 'lucide-react';
import { RequestButton } from '../components/RequestButton';
import LyricsSection from '../components/LyricsSection';
import GiftPicker from '../components/GiftPicker';
import useQualifiedView from '../hooks/useQualifiedView';

// Platform icons/colors for DSP links
const DSP_PLATFORMS = {
  spotify: { name: 'Spotify', color: 'text-green-500', bgColor: 'bg-green-500/10 hover:bg-green-500/20' },
  apple_music: { name: 'Apple Music', color: 'text-pink-500', bgColor: 'bg-pink-500/10 hover:bg-pink-500/20' },
  soundcloud: { name: 'SoundCloud', color: 'text-orange-500', bgColor: 'bg-orange-500/10 hover:bg-orange-500/20' },
  audiomack: { name: 'Audiomack', color: 'text-orange-400', bgColor: 'bg-orange-400/10 hover:bg-orange-400/20' },
  youtube: { name: 'YouTube', color: 'text-red-500', bgColor: 'bg-red-500/10 hover:bg-red-500/20' },
};

export default function TrackPage() {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState(null);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasRequested, setHasRequested] = useState(false);
  const [requestingChart, setRequestingChart] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  
  // Beat Store state
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [trackPricing, setTrackPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingForm, setPricingForm] = useState({ basic: '29.99', premium: '79.99', exclusive: '299.99' });
  
  const audioRef = useRef(null);
  const [giftPickerOpen, setGiftPickerOpen] = useState(false);

  // Qualified view tracking — fires once per session when thresholds hit
  const { reportProgress } = useQualifiedView({
    contentId: trackId,
    contentType: 'track',
    durationSeconds: duration,
  });

  useEffect(() => {
    fetchTrack();
  }, [trackId]);

  const fetchTrack = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/tracks/${trackId}`, { headers });
      
      if (!res.ok) {
        if (res.status === 404) {
          setError('Track not found');
        } else {
          setError('Failed to load track');
        }
        return;
      }
      
      const data = await res.json();
      console.log('[TrackPage] Track loaded:', data);
      setTrack(data);
      setIsLiked(data.is_liked || false);
      setLikeCount(data.likes || data.like_count || 0);
      
      // Record play
      try {
        await fetch(`${API}/tracks/${trackId}/play`, {
          method: 'POST',
          headers
        });
      } catch (e) {
        console.log('[TrackPage] Play count update failed:', e);
      }
    } catch (err) {
      console.error('[TrackPage] Fetch error:', err);
      setError('Failed to load track');
    } finally {
      setLoading(false);
    }
  };

  // Fetch pricing info
  useEffect(() => {
    if (!trackId) return;
    fetch(`${API}/beat-store/track/${trackId}/pricing`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setTrackPricing(d); })
      .catch(() => {});
  }, [trackId]);

  const isOwner = user && track && (
    user.user_id === track.user_id || 
    user.user_id === track.ownerUserId || 
    user.user_id === track.artist_id ||
    user.user_id === track.created_by ||
    (trackPricing && user.user_id === trackPricing.owner_id)
  );

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const res = await fetch(`${API}/beat-store/track/${trackId}/pricing`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          for_sale: true,
          basic_price: parseFloat(pricingForm.basic) || 29.99,
          premium_price: parseFloat(pricingForm.premium) || 79.99,
          exclusive_price: parseFloat(pricingForm.exclusive) || 299.99,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setTrackPricing(prev => ({ ...prev, for_sale: true, prices: data.custom_prices }));
        setShowPricingModal(false);
        toast.success('Beat is now listed for sale!');
      } else {
        toast.error('Failed to update pricing');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleRemoveFromSale = async () => {
    try {
      const res = await fetch(`${API}/beat-store/track/${trackId}/pricing`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ for_sale: false }),
      });
      if (res.ok) {
        setTrackPricing(prev => ({ ...prev, for_sale: false }));
        toast.success('Beat removed from sale');
      }
    } catch {
      toast.error('Failed to update');
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // iOS Safari: ensure load() is called before play() if src changed
      if (audioRef.current.readyState === 0) {
        audioRef.current.load();
      }
      audioRef.current.play().catch(e => {
        console.error('[TrackPage] Play error:', e?.name, e?.message);
        setIsPlaying(false);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to like tracks');
      return;
    }
    
    try {
      const endpoint = isLiked ? 'unlike' : 'like';
      await fetch(`${API}/tracks/${trackId}/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      setIsLiked(!isLiked);
      setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
    } catch (err) {
      toast.error('Failed to update like');
    }
  };

  const handleShare = async () => {
    const ogShareUrl = `${window.location.origin}/api/og/track/${trackId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: track?.title || 'Track',
          text: `Listen to ${track?.title} by ${track?.artist_name}`,
          url: ogShareUrl
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      try {
        await navigator.clipboard.writeText(ogShareUrl);
        toast.success('Link copied to clipboard');
      } catch {
        // Clipboard not available
      }
    }
  };

  const handleRequestChart = async () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to request tracks for charts');
      navigate('/login');
      return;
    }
    
    setRequestingChart(true);
    try {
      const res = await fetch(`${API}/requests/track`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ track_id: trackId })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to request track');
      }
      
      setHasRequested(true);
      toast.success('Track requested for Top 20! Your support helps artists climb the charts.');
    } catch (err) {
      if (err.message.includes('Already requested')) {
        setHasRequested(true);
        toast.info('You already requested this track today');
      } else if (err.message.includes('Daily request limit')) {
        toast.error('Daily request limit reached (20/day)');
      } else {
        toast.error(err.message || 'Failed to request track');
      }
    } finally {
      setRequestingChart(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get the playable URL with proper base URL handling
  const getPlayableUrl = () => {
    if (!track) return null;
    
    // Use server-computed playable_url if available
    if (track.playable_url) {
      if (track.playable_url.startsWith('/')) {
        return `${API_BASE_URL}${track.playable_url}`;
      }
      return track.playable_url;
    }
    
    // Fallback: check audio_url
    if (track.audio_url) {
      if (track.audio_url.startsWith('/')) {
        return `${API_BASE_URL}${track.audio_url}`;
      }
      return track.audio_url;
    }
    
    return null;
  };

  // Get external DSP URL for "Open in" buttons
  const getExternalUrl = () => {
    return track?.dsp_url || track?.spotify_url || track?.apple_music_url || track?.playable_url;
  };

  const playableUrl = getPlayableUrl();
  const playableType = track?.playable_type;
  const isPlayable = track?.is_playable;
  const dspPlatform = track?.dsp_platform;
  const platformInfo = dspPlatform ? DSP_PLATFORMS[dspPlatform] : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="track-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !track) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="track-page-error">
        <Card className="glass-card border-border p-8 text-center max-w-md">
          <Music className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Track Unavailable</h1>
          <p className="text-muted-foreground mb-6">
            {error || "This track is no longer available or has been removed."}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button onClick={() => navigate('/tracks')}>
              Browse Tracks
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="track-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button variant="ghost" size="sm" onClick={handleShare} data-testid="share-button">
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Track Info Card */}
        <Card className="glass-card border-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Cover Art */}
            <div className="w-full md:w-64 aspect-square rounded-xl overflow-hidden bg-muted/30 flex-shrink-0">
              {track.cover_url ? (
                <img 
                  src={track.cover_url} 
                  alt={track.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Music className="w-20 h-20 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Track Details */}
            <div className="flex-1 flex flex-col">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold mb-2" data-testid="track-title">{track.title}</h1>
                
                {/* Artist Link */}
                <Link 
                  to={getProfileUrl({ 
                    handle: track.artist_handle || track.handle, 
                    user_id: track.artist_id || track.user_id || track.created_by || track.uploaded_by 
                  })}
                  className="text-lg text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-2"
                  data-testid="artist-link"
                >
                  <UserAvatar 
                    src={track.artist_avatar} 
                    alt={track.artist_name}
                    size="sm"
                  />
                  {track.artist_name || 'Unknown Artist'}
                </Link>

                {/* Metadata */}
                <div className="flex flex-wrap gap-3 mt-4">
                  {track.genre && (
                    <Badge variant="outline">{track.genre}</Badge>
                  )}
                  {track.primary_genre_name && track.primary_genre_name !== track.genre && (
                    <Badge variant="outline">{track.primary_genre_name}</Badge>
                  )}
                  {dspPlatform && platformInfo && (
                    <Badge variant="outline" className={platformInfo.color}>
                      {platformInfo.name}
                    </Badge>
                  )}
                  {track.release_date && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(track.release_date).toLocaleDateString()}
                    </Badge>
                  )}
                  {track.duration > 0 && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(track.duration)}
                    </Badge>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 mt-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Play className="w-4 h-4" />
                    {track.plays || 0} plays
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    {likeCount} likes
                  </span>
                  {track.chart_position && (
                    <span className="flex items-center gap-1 text-yellow-500">
                      <BarChart3 className="w-4 h-4" />
                      #{track.chart_position} on charts
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 mt-6">
                {/* Primary Play Button - Logic based on playable_type */}
                {playableType === 'audio' && playableUrl ? (
                  <Button 
                    size="lg" 
                    className="gap-2"
                    onClick={handlePlayPause}
                    data-testid="play-button"
                  >
                    {isPlaying ? (
                      <>
                        <Pause className="w-5 h-5" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        Play
                      </>
                    )}
                  </Button>
                ) : playableType === 'embed' && track.embed_url ? (
                  <Button 
                    size="lg" 
                    className="gap-2"
                    onClick={() => {
                      const embedSection = document.getElementById('embed-player');
                      if (embedSection) embedSection.scrollIntoView({ behavior: 'smooth' });
                    }}
                    data-testid="play-button"
                  >
                    <Play className="w-5 h-5" />
                    Play
                  </Button>
                ) : getExternalUrl() ? (
                  <Button 
                    size="lg" 
                    className={`gap-2 ${platformInfo?.bgColor || ''}`}
                    onClick={() => window.open(getExternalUrl(), '_blank')}
                    data-testid="play-button"
                  >
                    <ExternalLink className="w-5 h-5" />
                    {platformInfo ? `Play on ${platformInfo.name}` : 'Listen Now'}
                  </Button>
                ) : (
                  <Button 
                    size="lg" 
                    className="gap-2"
                    disabled
                    data-testid="play-button-disabled"
                  >
                    <Music className="w-5 h-5" />
                    Not Available
                  </Button>
                )}
                
                <Button 
                  variant={isLiked ? "default" : "outline"}
                  size="lg"
                  onClick={handleLike}
                  className={isLiked ? "bg-red-500 hover:bg-red-600" : ""}
                  data-testid="like-button"
                >
                  <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                </Button>
                
                <Button variant="outline" size="lg" onClick={handleShare}>
                  <Share2 className="w-5 h-5" />
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setGiftPickerOpen(true)}
                  className="border-amber-500/40 hover:bg-amber-500/10 text-amber-400"
                  data-testid="track-gift-btn"
                  title="Send a gift"
                >
                  <Gift className="w-5 h-5" />
                </Button>
                
                <Button 
                  variant={hasRequested ? "secondary" : "default"}
                  size="lg"
                  onClick={handleRequestChart}
                  disabled={hasRequested || requestingChart}
                  className={hasRequested ? "" : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"}
                  data-testid="request-chart-button"
                >
                  {requestingChart ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trophy className="w-5 h-5" />
                  )}
                  <span className="ml-2 hidden sm:inline">{hasRequested ? 'Requested' : 'Top 20'}</span>
                </Button>

                {/* Request Button */}
                <RequestButton
                  targetType="song"
                  targetId={track.track_id}
                  targetName={track.title}
                  creatorName={track.artist_name}
                  variant="outline"
                  size="lg"
                />

                {/* Beat Store Buttons */}
                {isOwner ? (
                  <Button
                    variant={trackPricing?.for_sale ? "secondary" : "default"}
                    size="lg"
                    onClick={() => {
                      if (trackPricing?.for_sale) {
                        handleRemoveFromSale();
                      } else {
                        setShowPricingModal(true);
                      }
                    }}
                    className={trackPricing?.for_sale ? "" : "bg-[#34D399] hover:bg-[#2AB383] text-black"}
                    data-testid="sell-beat-btn"
                  >
                    <DollarSign className="w-5 h-5" />
                    <span className="ml-2 hidden sm:inline">{trackPricing?.for_sale ? 'Listed' : 'Sell Beat'}</span>
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    onClick={() => navigate(`/beat-store/${track.track_id}`)}
                    className="bg-[#007AFF] hover:bg-[#0066DD]"
                    data-testid="buy-beat-btn"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    <span className="ml-2 hidden sm:inline">Buy Beat</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Audio Player (for direct uploads / playable_type="audio") */}
        {playableType === 'audio' && playableUrl && (
          <Card className="glass-card border-border p-4 mb-6" data-testid="audio-player">
            <audio
              ref={audioRef}
              src={playableUrl}
              preload="auto"
              playsInline
              onTimeUpdate={(e) => {
                setCurrentTime(e.target.currentTime);
                // Report qualified view when thresholds hit
                if (reportProgress) {
                  reportProgress({
                    secondsWatched: e.target.currentTime,
                    isVisible: !document.hidden,
                    isAudible: !e.target.muted && e.target.volume > 0,
                  });
                }
              }}
              onLoadedMetadata={(e) => setDuration(e.target.duration)}
              onEnded={() => setIsPlaying(false)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div 
                className="h-2 bg-muted/50 rounded-full cursor-pointer"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  if (audioRef.current) {
                    audioRef.current.currentTime = percent * duration;
                  }
                }}
              >
                <div 
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => {
                if (audioRef.current) audioRef.current.currentTime -= 10;
              }}>
                <SkipBack className="w-5 h-5" />
              </Button>
              
              <Button 
                size="icon" 
                className="w-12 h-12 rounded-full"
                onClick={handlePlayPause}
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
              </Button>
              
              <Button variant="ghost" size="icon" onClick={() => {
                if (audioRef.current) audioRef.current.currentTime += 10;
              }}>
                <SkipForward className="w-5 h-5" />
              </Button>
              
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => {
                  setIsMuted(!isMuted);
                  if (audioRef.current) audioRef.current.muted = !isMuted;
                }}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
            </div>
          </Card>
        )}

        {/* Embed Player (for playable_type="embed") */}
        {playableType === 'embed' && track.embed_url && (
          <Card className="glass-card border-border p-4 mb-6" id="embed-player" data-testid="embed-player">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <Music className={`w-5 h-5 ${platformInfo?.color || ''}`} />
              {platformInfo ? `Play on ${platformInfo.name}` : 'Player'}
            </h3>
            
            {/* Spotify Embed */}
            {dspPlatform === 'spotify' && (
              <iframe
                src={track.embed_url}
                width="100%"
                height="152"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-lg"
                title={`${track.title} on Spotify`}
              />
            )}
            
            {/* YouTube Embed */}
            {dspPlatform === 'youtube' && (
              <div className="aspect-video">
                <iframe
                  src={track.embed_url}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded-lg"
                  title={`${track.title} on YouTube`}
                />
              </div>
            )}
            
            {/* SoundCloud Embed - uses widget */}
            {dspPlatform === 'soundcloud' && (
              <iframe
                width="100%"
                height="166"
                scrolling="no"
                frameBorder="no"
                allow="autoplay"
                src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(track.embed_url)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`}
                className="rounded-lg"
                title={`${track.title} on SoundCloud`}
              />
            )}
            
            {/* Generic embed fallback */}
            {!['spotify', 'youtube', 'soundcloud'].includes(dspPlatform) && (
              <div className="text-center py-4">
                <Button 
                  size="lg"
                  className={platformInfo?.bgColor || ''}
                  onClick={() => window.open(track.embed_url, '_blank')}
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Open Player
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* External DSP Links (for playable_type="external" or additional platforms) */}
        {(playableType === 'external' || track.spotify_url || track.apple_music_url) && (
          <Card className="glass-card border-border p-6 mb-6" data-testid="dsp-links">
            <h3 className="font-bold mb-4">Listen on Your Favorite Platform</h3>
            <div className="flex flex-wrap gap-3">
              {track.spotify_url && (
                <Button 
                  variant="outline" 
                  className={DSP_PLATFORMS.spotify.bgColor}
                  onClick={() => window.open(track.spotify_url, '_blank')}
                >
                  <Music className={`w-4 h-4 mr-2 ${DSP_PLATFORMS.spotify.color}`} />
                  Spotify
                </Button>
              )}
              {track.apple_music_url && (
                <Button 
                  variant="outline" 
                  className={DSP_PLATFORMS.apple_music.bgColor}
                  onClick={() => window.open(track.apple_music_url, '_blank')}
                >
                  <Music className={`w-4 h-4 mr-2 ${DSP_PLATFORMS.apple_music.color}`} />
                  Apple Music
                </Button>
              )}
              {track.soundcloud_url && (
                <Button 
                  variant="outline" 
                  className={DSP_PLATFORMS.soundcloud.bgColor}
                  onClick={() => window.open(track.soundcloud_url, '_blank')}
                >
                  <Music className={`w-4 h-4 mr-2 ${DSP_PLATFORMS.soundcloud.color}`} />
                  SoundCloud
                </Button>
              )}
              {track.youtube_url && (
                <Button 
                  variant="outline" 
                  className={DSP_PLATFORMS.youtube.bgColor}
                  onClick={() => window.open(track.youtube_url, '_blank')}
                >
                  <Music className={`w-4 h-4 mr-2 ${DSP_PLATFORMS.youtube.color}`} />
                  YouTube
                </Button>
              )}
              {track.dsp_url && !track.spotify_url && !track.apple_music_url && !track.soundcloud_url && !track.youtube_url && (
                <Button 
                  variant="outline" 
                  onClick={() => window.open(track.dsp_url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Listen Now
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* No Playback Warning - Only show if truly unplayable */}
        {!isPlayable && !getExternalUrl() && (
          <Card className="glass-card border-yellow-500/20 bg-yellow-500/5 p-6 mb-6" data-testid="no-playback-warning">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-yellow-500 mb-2">Playback Unavailable</h3>
                <p className="text-muted-foreground text-sm">
                  This track doesn't have a playable source configured. 
                  {user?.user_id === track.artist_id && (
                    <span> You can update the track settings to add a streaming link or upload an audio file.</span>
                  )}
                </p>
                {user?.user_id === track.artist_id && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => navigate('/dashboard/artist')}
                  >
                    Edit Track
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Description */}
        {track.description && (
          <Card className="glass-card border-border p-6">
            <h3 className="font-bold mb-3">About This Track</h3>
            <p className="text-muted-foreground whitespace-pre-wrap">{track.description}</p>
          </Card>
        )}

        {/* Lyrics — below description, above comments */}
        <LyricsSection lyrics={track.lyrics} songTitle={track.title} />
      </div>

      {/* Gift picker modal */}
      <GiftPicker
        open={giftPickerOpen}
        onOpenChange={setGiftPickerOpen}
        contentId={trackId}
        contentType="track"
        creatorUserId={track.user_id || track.artist_user_id}
        creatorName={track.artist_name}
      />

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="pricing-modal">
          <div className="bg-[#141414] border border-white/10 rounded-2xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Set Your Prices</h3>
                <p className="text-xs text-zinc-500">Customize license pricing for this beat</p>
              </div>
              <button onClick={() => setShowPricingModal(false)} className="text-zinc-500 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {[
                { key: 'basic', label: 'Basic License', desc: 'MP3 lease, up to 5K streams', default: '29.99' },
                { key: 'premium', label: 'Premium License', desc: 'WAV + MP3, up to 50K streams', default: '79.99' },
                { key: 'exclusive', label: 'Exclusive Rights', desc: 'Full ownership transfer', default: '299.99' },
              ].map(tier => (
                <div key={tier.key} className="bg-white/5 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <p className="text-sm font-medium text-white">{tier.label}</p>
                      <p className="text-[10px] text-zinc-500">{tier.desc}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-zinc-500 text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.99"
                        value={pricingForm[tier.key]}
                        onChange={(e) => setPricingForm(prev => ({ ...prev, [tier.key]: e.target.value }))}
                        className="w-24 bg-[#1C1C1E] border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-right font-mono"
                        data-testid={`price-input-${tier.key}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 border-white/10" onClick={() => setShowPricingModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSavePricing}
                disabled={savingPricing}
                className="flex-1 bg-[#34D399] hover:bg-[#2AB383] text-black font-semibold"
                data-testid="save-pricing-btn"
              >
                {savingPricing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                List for Sale
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
